import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { errorMessage } from '../api/client';
import type { MandateInput, MandateView } from '../api/types';
import { fmtDate } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtCents, mandateKindLabels, todayISO } from './format';

type Kind = MandateInput['kind'];

function priceFor(v: MandateView, kind: Kind) {
  const q = kind === 'QUALIFIED' ? v.qualified : v.general;
  return q ? q.price_cents : null;
}

// MandateSection: „Auftrag erteilt“ am Termin (Konzept 10, OFFENE_FRAGEN B-22). Der Makler des Termins (oder Leitung und
// Support) meldet den schriftlichen Maklerauftrag; für Partner im Auftragspreis steht der Preis vorab da und wird beim
// Melden eingefroren. Aus „allgemein“ wird mit der Aufwertung „qualifiziert“ (berechnet wird nur der Unterschied).
export function MandateSection({ appointmentId }: { appointmentId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const qc = useQueryClient();
  const key = projectKeys.mandate(project.id, appointmentId);
  const q = useQuery({ queryKey: key, queryFn: ({ signal }) => papi.appointmentMandate(appointmentId, signal), retry: false });
  const [form, setForm] = useState<{ kind: Kind; grantedOn: string; term: string; note: string } | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const onSaved = (data: MandateView) => {
    qc.setQueryData(key, data);
    void qc.invalidateQueries({ queryKey: ['projects', project.id, 'mandates'] });
    setForm(null);
  };
  const report = useMutation({
    mutationFn: (f: NonNullable<typeof form>) =>
      papi.reportMandate(appointmentId, { kind: f.kind, granted_on: f.grantedOn, term_months: f.term ? Number(f.term) : null, note: f.note }),
    onSuccess: onSaved,
  });
  const cancel = useMutation({
    mutationFn: (r: string) => papi.cancelMandate(q.data?.mandate?.id ?? '', r),
    onSuccess: () => {
      setReason(null);
      void q.refetch();
    },
  });

  if (q.isPending || q.isError) return null;
  const v = q.data;
  const m = v.mandate;
  const partner = v.contract_model === 'ORDER_PRICE';
  const upgrade = !!m && m.kind === 'GENERAL';

  return (
    <section className="card mandate" aria-labelledby="mandate-title">
      <h2 id="mandate-title">Auftrag erteilt?</h2>
      {m ? (
        <>
          <p>
            <span className="badge badge-ok">{mandateKindLabels[m.kind]}</span> erteilt am {fmtDate(m.granted_on)}
            {m.term_months ? ` · Laufzeit ${m.term_months} Monate` : ''}
            {m.reported_by_name && <span className="muted"> · gemeldet von {m.reported_by_name}</span>}
          </p>
          {m.price_cents != null && (
            <p>
              Auftragspreis <strong className="price-strong">{fmtCents(m.price_cents)}</strong>
              {m.open_cents > 0 && <span className="muted"> · {fmtCents(m.open_cents)} zur Abrechnung vorgemerkt</span>}
              {m.is_test && <span className="muted"> · Testkonto – wird nicht abgerechnet</span>}
              {m.pricing && (
                <small className="muted block">
                  {m.pricing.region.basis} · {m.pricing.object.basis}
                </small>
              )}
            </p>
          )}
          {m.note && <p className="muted">{m.note}</p>}
        </>
      ) : (
        <p className="muted">
          Hat der Eigentümer einen schriftlichen Maklerauftrag zum Verkauf erteilt? Bitte hier melden
          {partner && !v.is_test && ' – der Auftragspreis steht vorab da und wird mit der Meldung zur Abrechnung vorgemerkt.'}
          {partner && v.is_test && ' – der Auftragspreis steht vorab da. Testkonto: Es wird nichts abgerechnet.'}
          {!partner && '.'}
        </p>
      )}
      {v.can_report &&
        (!m || upgrade) &&
        (form ? (
          <form
            className="contract-form"
            onSubmit={(e) => {
              e.preventDefault();
              report.mutate(form);
            }}
          >
            <fieldset>
              <legend>Art des Auftrags</legend>
              {(['GENERAL', 'QUALIFIED'] as const)
                .filter((k) => !upgrade || k === 'QUALIFIED')
                .map((k) => (
                  <label key={k} className="checkbox">
                    <input type="radio" name="mandate-kind" checked={form.kind === k} onChange={() => setForm({ ...form, kind: k })} />
                    <span>
                      <strong>{mandateKindLabels[k]}</strong>
                      {partner && priceFor(v, k) != null && ` – ${fmtCents(priceFor(v, k) ?? 0)}`}
                      {upgrade && partner && m?.price_cents != null && priceFor(v, k) != null && (
                        <span className="muted"> (berechnet wird der Unterschied)</span>
                      )}
                    </span>
                  </label>
                ))}
            </fieldset>
            {!upgrade && (
              <div className="form-grid">
                <label className="field">
                  <span>Erteilt am</span>
                  <input type="date" value={form.grantedOn} max={todayISO()} onChange={(e) => setForm({ ...form, grantedOn: e.target.value })} required />
                </label>
                <label className="field">
                  <span>Laufzeit in Monaten (optional)</span>
                  <input type="number" min={1} max={120} value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} />
                </label>
              </div>
            )}
            <label className="field">
              <span>Notiz (optional)</span>
              <input value={form.note} maxLength={2000} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
            {report.isError && <p className="confirmation-failed">{errorMessage(report.error)}</p>}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={report.isPending}>
                {upgrade ? 'Aufwertung melden' : 'Auftrag melden'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setForm(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            className="btn btn-small"
            onClick={() => setForm({ kind: upgrade ? 'QUALIFIED' : 'GENERAL', grantedOn: m?.granted_on ?? todayISO(), term: '', note: '' })}
          >
            {upgrade ? 'Auf Alleinauftrag aufwerten' : 'Auftrag erteilt melden'}
          </button>
        ))}
      {m &&
        can('project.manage') &&
        (reason === null ? (
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setReason('')}>
            Irrtümliche Meldung zurücknehmen
          </button>
        ) : (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              cancel.mutate(reason);
            }}
          >
            <label className="field">
              <span>Grund (nur bei irrtümlicher Meldung – kündigt der Eigentümer später, bleibt der Preis)</span>
              <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} required />
            </label>
            <button type="submit" className="btn btn-small btn-danger" disabled={cancel.isPending || !reason.trim()}>
              Zurücknehmen
            </button>
            {cancel.isError && <span className="confirmation-failed">{errorMessage(cancel.error)}</span>}
          </form>
        ))}
    </section>
  );
}
